import type { User } from '../auth/types';
import { hasReceptionAllBranchesGrant } from '../auth/receptionPermissions';
import { canAccessStaffRoute, routeKeyFromPath } from '../features/summer-training/auth/roleAccess';
import type { RoutePolicy } from './types';

const SALES_ROLES = new Set([
  'sales',
  'agent',
  'sales_manager',
  'tele_sales',
  'ghosted_sales',
  'general_manager',
]);

export function isRouteAuthorized(policy: RoutePolicy, user: User | null | undefined): boolean {
  const role = String(user?.role ?? '').trim().toLowerCase();
  const isAuthenticated = Boolean(user);
  const hasReceptionRole =
    role.includes('reception')
    || (role.includes('manager') && role !== 'sales_manager')
    || role.includes('hr');
  const isGeneralManager = role === 'general_manager';
  const isSuperReception = role === 'super_reception' || isGeneralManager || hasReceptionAllBranchesGrant(user);

  switch (policy.guard) {
    case 'public':
      return true;
    case 'authenticated':
      return isAuthenticated;
    case 'staff':
      return isAuthenticated && Boolean(role) && role !== 'student'
        && !(role === 'moderator' && policy.routerPath.startsWith('/marketing/work/'));
    case 'reception':
      return isAuthenticated && hasReceptionRole;
    case 'manager':
      return isAuthenticated && role.includes('manager') && role !== 'sales_manager';
    case 'hr':
      return isAuthenticated && (role.includes('hr') || isGeneralManager);
    case 'accountant':
      return isAuthenticated && (role === 'accountant' || isGeneralManager);
    case 'sales':
      return isAuthenticated && SALES_ROLES.has(role);
    case 'marketing':
      return isAuthenticated && (role === 'marketing' || role === 'moderator' || isGeneralManager);
    case 'developer':
      return isAuthenticated && (role === 'developer' || user?.is_developer === true);
    case 'student':
      return isAuthenticated && role === 'student';
    case 'instructor':
      return isAuthenticated && role.includes('instructor');
    case 'reception-finance':
      return isAuthenticated && hasReceptionRole && (isSuperReception || role === 'manager');
    case 'reception-language':
      return isAuthenticated && (role === 'reception' || isSuperReception);
    case 'reception-super':
      return isAuthenticated && hasReceptionRole && isSuperReception;
    case 'summer-student':
      return isAuthenticated && role === 'student';
    case 'summer-staff':
      return isAuthenticated && canAccessStaffRoute(role, routeKeyFromPath(policy.routerPath));
  }
}
